import { Box, Divider, Link, Text } from '@factorialco/deprecated-design-system'
import { BudgetMovementFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface Props {
  movement: BudgetMovementFragment
}

interface LabelProps {
  label: string
  value: string
}

const LabelValue = ({ label, value }: LabelProps) => (
  <Box gap="s12" flexGrow>
    <Text.Normal color="grey1000" weight="medium" size="200">
      {label}
    </Text.Normal>
    <Text.Normal color="grey1000">{value}</Text.Normal>
  </Box>
)

const InfoTab = ({ movement }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useExpensesNavigation()
  const {
    expensable,
    sourceCurrency,
    targetCurrency,
    budget,
    employee,
    resourceId,
    exchangeRate,
    convertedAt,
    amountCents,
  } = movement

  const originalAmount = Number(expensable?.reimbursableAmount || expensable?.amount)
  const originalCurrency = expensable?.reimbursableCurrency || expensable?.currency

  const movementCurrencyConverted = sourceCurrency !== targetCurrency
  const hasBudgetCurrency = targetCurrency === budget?.currency
  const convertedToBudgetCurrency = movementCurrencyConverted && hasBudgetCurrency
  const notConvertedToBudgetCurrency = !movementCurrencyConverted && !hasBudgetCurrency

  const formatedRate =
    convertedToBudgetCurrency && exchangeRate
      ? amountWithCurrency(Math.round(exchangeRate * 100), targetCurrency, locale)
      : 'Not available'

  return (
    <Box gap="s24" paddingTop="s24">
      {employee && (
        <>
          <Box gap="s24">
            <Box gap="s12">
              <Text.Normal color="grey1000" weight="medium" size="200">
                {i18n.t('common.employee')}
              </Text.Normal>
              <Text.Normal color="grey1000">{employee.fullName}</Text.Normal>
            </Box>
            <Box gap="s12">
              <Text.Normal color="grey1000" weight="medium" size="200">
                {i18n.t('common.legal_entity')}
              </Text.Normal>
              <Text.Normal color="grey1000">{employee.legalEntity?.legalName}</Text.Normal>
            </Box>
          </Box>

          <Divider />
        </>
      )}

      <Box gap="s8">
        <Text.Normal color="grey1000" weight="semibold">
          {i18n.t('budgets.detail.tabs.spendings.sidepanel.source')}
        </Text.Normal>
        <Link.Router external to={navigation.helpers.expensableDetail(resourceId)}>
          {i18n.t('budgets.detail.tabs.spendings.sidepanel.link_to')}
        </Link.Router>
      </Box>

      <Divider />

      {(convertedToBudgetCurrency || notConvertedToBudgetCurrency) && (
        <>
          <Box gap="s8">
            <Text.Normal color="grey1000" weight="semibold">
              {i18n.t('budgets.detail.tabs.spendings.sidepanel.currency_conversion')}
            </Text.Normal>
            <Text.Normal color="grey1000">
              {i18n.t('budgets.detail.tabs.spendings.sidepanel.currency_provider')}
            </Text.Normal>
          </Box>

          <Box gap="s24" flexDirection="row">
            <LabelValue
              label={i18n.t('budgets.detail.tabs.spendings.sidepanel.rate')}
              value={`${amountWithCurrency(100, sourceCurrency, locale)} = ${formatedRate}`}
            />
            <LabelValue label="Date" value={dateBackendDateTimeToUI(convertedAt)} />
          </Box>

          {convertedToBudgetCurrency && originalCurrency && amountCents && (
            <Box gap="s24" flexDirection="row">
              <LabelValue
                label={i18n.t('budgets.detail.tabs.spendings.sidepanel.original_amount')}
                value={amountWithCurrency(originalAmount, originalCurrency, locale)}
              />
              <LabelValue
                label={i18n.t('budgets.detail.tabs.spendings.sidepanel.converted_amount')}
                value={amountWithCurrency(amountCents, targetCurrency, locale)}
              />
            </Box>
          )}
          <Divider />
        </>
      )}
    </Box>
  )
}
export default InfoTab
